// Step 8: the first test. Once a few staff have joined, the ops centre sends the
// monthly test from the tablet and this page shows what happened, checking every 10 s.
// Before that, one go-live step is ours: adding the site to the sending service's own
// list of sites, which it keeps apart from the database on purpose.

import { useEffect, useState } from 'react';
import { api } from '../../api.ts';
import { phones, when } from '../../format.ts';
import type { AlertRow, HealthView, SiteView } from '../../types.ts';
import { useLoad } from '../../ui.tsx';

export function TestStep({ site }: { site: SiteView }) {
  const health = useLoad(() => api<HealthView>('GET', `/sites/${site.siteId}/health`), [site.siteId]);
  const alerts = useLoad(() => api<{ alerts: AlertRow[] }>('GET', `/sites/${site.siteId}/alerts`), [site.siteId]);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!site.live) return;
    const t = setInterval(() => {
      health.reload();
      alerts.reload();
    }, 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.live, site.siteId]);

  if (!site.live) {
    return (
      <div className="note amber" style={{ maxWidth: 760 }}>
        The first test happens after the set-up is approved on the ops tablet and staff have joined with the poster.
      </div>
    );
  }
  const h = health.data;
  const test = alerts.data?.alerts.find((a) => a.test);
  const everSent = alerts.data?.alerts.some((a) => a.sentAt || a.attempted > 0) ?? false;
  const listLine = `"${site.siteId}": "${site.anchor}"`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(listLine);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  let result: { text: string; tone: 'green' | 'amber'; note?: string } | null = null;
  if (test && !test.sentAt && test.attempted === 0) {
    result = {
      text: 'Received, but not sent to phones yet',
      tone: 'amber',
      note: "If this doesn't change within a minute, check the site is on the sending service's list (above).",
    };
  } else if (test && test.attempted === 0) {
    result = { text: 'Sent, but no phones had joined yet', tone: 'amber' };
  } else if (test) {
    const missed = test.attempted - test.accepted;
    result = {
      text: `Apple and Google accepted it for ${test.accepted} of ${test.attempted} phone${test.attempted === 1 ? '' : 's'}`,
      tone: missed ? 'amber' : 'green',
      note: `${!test.sentAt && missed ? `Still trying the other ${missed}. ` : ''}Accepted means Apple or Google took it for delivery. Ask a few staff to confirm their phone rang.`,
    };
  }

  return (
    <>
      {!everSent && (
        <div className="note amber stack" style={{ maxWidth: 760, gap: 10 }}>
          <strong>Before the first test (Z.A.S. team): add this site to the sending service's list</strong>
          <span>
            The sending service only sends for sites on its own list, which is kept apart from our database on purpose: someone who broke into the database still couldn't make phones
            ring. Until this site is added, its alerts reach our server but not staff phones.
          </span>
          <span className="small">Add this line to the list (the ZAS_SITE_ANCHORS setting), then restart the sending service:</span>
          <div className="row wrap">
            <code className="mono small" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', wordBreak: 'break-all' }}>
              {listLine}
            </code>
            <button className="btn small" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
      <p className="muted" style={{ maxWidth: 760 }}>
        Once a few staff have joined, ask the ops centre to send the monthly test from the tablet. Every phone that has joined should get it.
      </p>
      <div className="grid2" style={{ maxWidth: 760 }}>
        <div className="card">
          <span className="card-label">Phones joined so far</span>
          <span className="card-value mono">{h ? phones(h.phonesIos, h.phonesAndroid) : '…'}</span>
          <span className="muted small">Counted, never named</span>
        </div>
        <div className="card">
          <span className="card-label">Test</span>
          {!test || !result ? (
            <span className="card-value amber">Waiting for the ops centre…</span>
          ) : (
            <>
              <span className={`card-value ${result.tone}`}>{result.text}</span>
              <span className="muted small">
                Sent from the tablet {when(test.issuedAt)}
                {test.tabletLabel ? ` (${test.tabletLabel})` : ''}
                {test.unregistered ? ` · ${test.unregistered} phone${test.unregistered === 1 ? ' has' : 's have'} left` : ''}
                {test.failed ? ` · ${test.failed} failed` : ''}
              </span>
            </>
          )}
        </div>
      </div>
      {result?.note && <p className="faint small">{result.note}</p>}
      <p className="faint small">This page checks again every 10 seconds.</p>
    </>
  );
}
